import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
} from '@mui/material';
import { fetchPetsDashboard, handleError } from '../utils/lib';
import PetsRecentList from '../layouts/PetsRecentList';
import WaitingRoomsList from '../layouts/WaitingRoomsList';
import MainPetsAgendaList from '../layouts/MainPetsAgendaList';
import { useAppBar } from '../providers/AppBarProvider';
import { useConfig } from '../providers/ConfigProvider';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../providers/SnackBarProvider';
import axios from 'axios';
import useAgendaActions from '../hooks/useAgendaActions';
import { useTranslation } from 'react-i18next';

function MainPets() {
  const [lastMovements, setLastMovements] = useState([]);
  const [waitingRooms, setWaitingRooms] = useState([]);
  const [agendaItems, setAgendaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const {updateTitle, toggleFab} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const snackbar = useSnackbar();
  const { t } = useTranslation();

  // Usar el hook de acciones de agenda
  const {
    postponeDialog,
    completeDialog,
    handleReschedule,
    handlePostponeClose,
    handlePostponeConfirm,
    handleComplete,
    handleCompleteClose,
    handleCompleteConfirm,
    handleDateChange,
    AgendaDialogs
  } = useAgendaActions(
    // Callback onSuccess para recargar el dashboard después de completar/posponer
    async () => {
      try {
        const response = await fetchPetsDashboard();
        setLastMovements(response.last_movements);
        setWaitingRooms(response.in_waiting_rooms);
        setAgendaItems(response.in_agenda);
      } catch (error) {
        handleError(error, snackbar);
      }
    }
    ,snackbar
  );

  useEffect(() => {
    toggleFab(true);
    const fetchData = async () => {
      try {
        const response = await fetchPetsDashboard();
        setLastMovements(response.last_movements);
        setWaitingRooms(response.in_waiting_rooms);
        setAgendaItems(response.in_agenda);
        updateTitle('', config.vet.pet_naming_es_plural);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const onClick = (pet) => {
    navigate('/main/view-pet', { state: { id: pet.id, updateLastView: false } });
  }

  const handleWaitingRoomComplete = async (room) => {
    try {
      await axios.put(`/api/waiting-room/${room.id}/complete`);
      // Actualizar la lista después de completar
      setWaitingRooms(waitingRooms.filter(r => r.id !== room.id));
    } catch (error) {
      console.error('Error al marcar como atendido:', error);
    }
  };

  const handleWaitingRoomDelete = async (room) => {
    try {
      await axios.delete(`/api/waiting-room/${room.id}`);
      // Actualizar la lista después de eliminar
      setWaitingRooms(waitingRooms.filter(r => r.id !== room.id));
    } catch (error) {
      console.error('Error al eliminar de la sala de espera:', error);
    }
  };

  if (isLoading || isLoadingConfig) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );
  
  if (lastMovements.length === 0 && waitingRooms.length === 0 && agendaItems.length === 0) return (
    <Container maxWidth="xl">
      <Typography
        variant="subtitle1"
        >
          {t('main.nothingHere')}
      </Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl">
      {waitingRooms.length > 0 && (
        <WaitingRoomsList
          waitingRooms={waitingRooms}
          onClick={onClick}
          onComplete={handleWaitingRoomComplete}
          onDelete={handleWaitingRoomDelete}
        />
      )}
      
      {agendaItems.length > 0 && (
        <MainPetsAgendaList
          agendaItems={agendaItems}
          onClick={onClick}
          onPostpone={handleReschedule}
          onComplete={handleComplete}
          config={config}
        />
      )}

      {lastMovements.length > 0 && (
        <PetsRecentList
          pets={lastMovements}
          onClick={onClick}
          isMultiUser={config.vet.multiuser > 1}
        />
      )}



      <AgendaDialogs />
    </Container>
  );
}

export default MainPets;

